import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import { formatCredits, formatCreditsWithUnit, formatNumber, formatPrice } from '@lib/utils/units';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { Product, Subscription } from '@stores/Billing';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import css from './ChangeSubscriptionConfirmationModal.module.scss';

export interface ChangeSubscriptionConfirmationModalRef {
  open: () => void;
  close: () => void;
}

interface ChangeSubscriptionConfirmationModalProps {
  currentSubscription: Subscription | undefined;
  newSubscription: Product | null;
  action: 'downgrade' | 'upgrade' | null;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
}

export const ChangeSubscriptionConfirmationModal = forwardRef<
  ChangeSubscriptionConfirmationModalRef,
  ChangeSubscriptionConfirmationModalProps
>(({ currentSubscription, newSubscription, action, onConfirm, onCancel }, ref) => {
  const [open, setOpen] = useState(false);
  const confirmedRef = useRef(false);

  useModalRegistration('change-subscription', open);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (!confirmedRef.current && newSubscription && action) {
      trackingStore.checkoutCancelled({
        productId: newSubscription.id,
        productType: 'subscription',
        action,
      });
    }
    confirmedRef.current = false;
    setOpen(false);
    onCancel?.();
  };

  const handleConfirm = async () => {
    try {
      await onConfirm();
      confirmedRef.current = true;
      handleClose();
    } catch (error) {
      console.error('Error confirming subscription change:', error);
      // Error will be shown via toast by the calling function
    }
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const title = 'Confirm Subscription Change';
  const confirmText = action === 'upgrade' ? 'Yes, pay now.' : 'Yes, downgrade.';
  const cancelText = 'Cancel';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className={css.modal} onClick={(e) => e.stopPropagation()}>
        <IconButton className={css.closeButton} onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
        <h2>{title}</h2>
        <div className={css.message}>
          <div className={css.confirmationModalContent}>
            <span>
              Are you sure you want to {action} your subscription to {newSubscription?.name}?
            </span>
            <div className={css.subscriptionsDetails}>
              <span className={css.currentSubscription}>
                <span className={css.subscriptionTitle}>
                  Your current subscription is {currentSubscription?.product?.name}:
                </span>
                <ul>
                  <li>
                    Cost:{' '}
                    {currentSubscription?.product?.price &&
                      formatPrice(currentSubscription.product.price)}{' '}
                    per {currentSubscription?.product?.price.interval}
                  </li>
                  <li>
                    Credits: {formatCredits(currentSubscription?.product?.quota.credits)} per{' '}
                    {currentSubscription?.product?.price.interval}
                  </li>
                  <li>
                    Storage: {formatNumber(currentSubscription?.product?.quota.storageHours ?? 0)}{' '}
                    hours
                  </li>
                </ul>
              </span>
              <span className={css.newSubscription}>
                <span className={css.subscriptionTitle}>
                  Your new subscription will be {newSubscription?.name}:
                </span>
                <ul>
                  <li>
                    Cost: {newSubscription?.price && formatPrice(newSubscription.price)} per{' '}
                    {newSubscription?.price.interval}
                  </li>
                  <li>
                    Credits: {formatCredits(newSubscription?.quota.credits)} per{' '}
                    {newSubscription?.price.interval}
                  </li>
                  <li>Storage: {formatNumber(newSubscription?.quota.storageHours ?? 0)} hours</li>
                </ul>
              </span>
            </div>
            <div>
              {action === 'upgrade' && (
                <div className={css.actionDetails}>
                  <h3>A few moments after you confirm:</h3>
                  <ul>
                    <li>
                      You will be charged{' '}
                      {newSubscription?.price && formatPrice(newSubscription.price)}.
                    </li>
                    <li>
                      You will receive {formatCreditsWithUnit(newSubscription?.quota.credits)} so
                      that you can get back to using StarZero.
                    </li>
                    <li>
                      You will be able to store{' '}
                      {formatNumber(newSubscription?.quota.storageHours ?? 0)} hours of media.
                    </li>
                  </ul>
                </div>
              )}
              {action === 'downgrade' && (
                <div className={css.actionDetails}>
                  <h3>At the start of the next billing cycle:</h3>
                  <ul>
                    <li>
                      You will be charged{' '}
                      {newSubscription?.price && formatPrice(newSubscription.price)}.
                    </li>
                    <li>
                      Your storage allowance will be lowered to{' '}
                      {formatNumber(newSubscription?.quota.storageHours ?? 0)} hours of media.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={css.actions}>
          <ActionButton variant="secondary" onClick={handleClose}>
            {cancelText}
          </ActionButton>
          <LoadingActionButton
            variant="primary"
            onClick={handleConfirm}
            loadingText="Processing..."
          >
            {confirmText}
          </LoadingActionButton>
        </div>
      </div>
    </Modal>
  );
});
